import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/hooks/use-profile";
import { Navigate, useLocation } from "react-router-dom";
import { planMeetsMinimum, type PlanKey } from "@/lib/stripe-plans";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireSubscription?: boolean;
  minPlan?: PlanKey;
}

export default function ProtectedRoute({ children, requireSubscription = false, minPlan }: ProtectedRouteProps) {
  const { user, loading, subscribed, subscriptionLoading, isTrial, effectivePlan } = useAuth();
  const { data: profile, isLoading: profileLoading } = useProfile();
  const location = useLocation();

  if (loading || profileLoading || subscriptionLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Allow subscription page and onboarding without restrictions
  const isSubscriptionRoute = location.pathname === "/subscription";
  const isOnboardingRoute = location.pathname === "/onboarding";

  // Show onboarding only on first login (no company_name and no industry chosen yet)
  const needsOnboarding = profile && !profile.company_name && profile.industry === "other";

  if (needsOnboarding && !isOnboardingRoute && !isSubscriptionRoute) {
    return <Navigate to="/onboarding" replace />;
  }

  // Block access to premium features without active subscription or trial
  if (requireSubscription && !subscribed && !isTrial) {
    return <Navigate to="/subscription" replace />;
  }

  // Plan-tier gating: user must be on `minPlan` or higher
  if (minPlan && !planMeetsMinimum(effectivePlan, minPlan)) {
    return <Navigate to={`/subscription?upgrade=${minPlan}&from=${encodeURIComponent(location.pathname)}`} replace />;
  }

  return <>{children}</>;
}
